/* eslint-disable @tanstack/query/exhaustive-deps */
import { useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PetitionDetailsPageData } from 'src/app/pages/petitionGamma/details/pageContext';

import { shareVisualsProcessing as isProcessing } from '../../shared';

import type { PetitionShareVisualsReturn } from './api';
import { getPetitionShareVisuals } from './api';

type Petition = Pick<PetitionDetailsPageData['petition'], 'slug' | 'shareVisuals'>;

const QUERY_KEY = 'share-visuals';
const CACHE_TIME_MS = 5 * 60 * 1000;
const REFETCH_INTERVAL_MS = 1000;
const MAX_REFETCH_COUNT = 10;

// This file is a duplicate of pages/starterDashboard/shared/hooks/useTriggerShareVisualModal/useShareVisualModal/components/ShareVisualModalBody/hooks/useShareVisualModalBody/hooks/useShareVisuals/index.ts
// This file exists because refactoring the code in the starter dashboard would be a large task and we (the signature growth team) want to introduce this feature in the petition gamma page as an experiment.

// We avoid using setState because it causes a re-render which complicates the logic.
export function useShareVisuals({ petition }: { petition: Petition }) {
	const utilityContext = useUtilityContext();

	// Count the number of times we have attempted to fetch the data.  Once we reach
	// the maximum refetches we disable the query and show an appropriate message to the user.
	const fetchCount = useRef(0);
	const isTimeout = fetchCount.current >= MAX_REFETCH_COUNT;

	// If the modal has not been opened, we will seed the `cache` with the initial `pageData` share visuals.
	// Once the modal has been opened (i.e. any subsequent re-opens), we will switch to using the cache,
	// since the `pageData` never gets refreshed with the values fetched by `useQuery`.
	// If the cache expires and the `pageData` hasn't been refreshed , the user will briefly see the loading spinner.
	const cachedShareVisuals = useQueryClient().getQueryData<PetitionShareVisualsReturn>([QUERY_KEY, petition.slug]);
	const initialShareVisuals = cachedShareVisuals ?? petition.shareVisuals;

	const processing = isProcessing(initialShareVisuals);
	const loaded = initialShareVisuals.length > 0 && !processing;
	const queryEnabled = processing && !isTimeout;

	const responseState = useQuery<PetitionShareVisualsReturn>({
		queryKey: [QUERY_KEY, petition.slug],
		queryFn: async () => {
			fetchCount.current += 1;
			return getPetitionShareVisuals(utilityContext, petition.slug);
		},
		enabled: queryEnabled,
		refetchInterval: queryEnabled ? REFETCH_INTERVAL_MS : false, // Refetch until we timeout
		initialData: initialShareVisuals,
		staleTime: loaded ? CACHE_TIME_MS : 0, // Increase stale time once loaded
		gcTime: CACHE_TIME_MS,
	});

	return {
		...responseState,
		isTimeout,
	};
}
